import { ArrowUpRight, CalendarDays, Camera, ExternalLink, Images, X } from 'lucide-react'
import type { KeyboardEvent, MouseEvent, RefObject } from 'react'
import { ResponsiveImage } from './ResponsiveImage'
import type { TeamMember } from '../data/site'

type TeamProfileDialogProps = {
  member: TeamMember
  index: number
  closeButtonRef: RefObject<HTMLButtonElement | null>
  dialogRef: RefObject<HTMLElement | null>
  onClose: () => void
  onKeyDown: (event: KeyboardEvent<HTMLElement>) => void
}

export function TeamProfileDialog({ member, index, closeButtonRef, dialogRef, onClose, onKeyDown }: TeamProfileDialogProps) {
  const hasPhoto = Boolean(member.image)
  const number = String(index + 1).padStart(2, '0')
  const biography = member.bio.length ? member.bio : [`Contact the salon to learn more or book with ${member.name}.`]
  const profileLinks = [
    ...(member.bookingUrl ? [{ label: `Book with ${member.name}`, url: member.bookingUrl, icon: CalendarDays }] : []),
    ...(member.instagram ? [{ label: 'Instagram', url: member.instagram, icon: Camera }] : []),
    ...(member.websiteUrl ? [{ label: 'Visit Website', url: member.websiteUrl, icon: ExternalLink }] : []),
    ...(member.portfolioUrl ? [{ label: 'View Portfolio', url: member.portfolioUrl, icon: Images }] : []),
    ...(member.links ?? []).map((link) => ({ ...link, icon: ArrowUpRight })),
  ]

  const closeFromBackdrop = (event: MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) onClose()
  }

  return (
    <div className="team-profile-dialog-backdrop" onMouseDown={closeFromBackdrop}>
      <section
        className="team-profile-dialog"
        id="team-profile-dialog"
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="team-profile-dialog-title"
        onKeyDown={onKeyDown}
      >
        <button className="team-profile-dialog__close" type="button" ref={closeButtonRef} onClick={onClose} aria-label="Close profile">
          <X aria-hidden="true" />
        </button>
        <div className={`team-profile-dialog__portrait${hasPhoto ? '' : ' team-profile-dialog__portrait--empty'}`}>
          {hasPhoto ? (
            <ResponsiveImage image={member.image!} alt={member.name} sizes="(max-width: 620px) 94vw, 42vw" objectFit="contain" objectPosition="center top" priority />
          ) : (
            <span className="team-profile-dialog__initials" aria-hidden="true">{member.name.split(' ').map((part) => part[0]).join('')}</span>
          )}
          <span className="team-profile-dialog__number" aria-hidden="true">{number}</span>
        </div>
        <div className="team-profile-dialog__content" role="region" aria-label={`${member.name} profile details`} tabIndex={0}>
          <span className="eyebrow">Meet the Professional</span>
          <h2 id="team-profile-dialog-title">{member.name}</h2>
          <p className="team-profile-dialog__role">{member.role}</p>
          <div className="team-profile-dialog__bio">
            {biography.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
          </div>
          {profileLinks.length > 0 && (
            <nav className="team-profile-dialog__actions" aria-label={`${member.name} profile links`}>
              {profileLinks.map(({ label, url, icon: Icon }) => (
                <a className="team-profile-dialog__action-link" href={url} target="_blank" rel="noopener noreferrer" key={`${label}-${url}`}>
                  <Icon aria-hidden="true" />
                  <span>{label}</span>
                  <ArrowUpRight className="team-profile-dialog__action-arrow" aria-hidden="true" />
                </a>
              ))}
            </nav>
          )}
        </div>
      </section>
    </div>
  )
}
